/**
 * The top bar: what you have, where it is going, and the one global lever.
 *
 * Stage 4.2's exit criterion is that a player can make every decision without
 * opening a console, so this panel carries more than four numbers. Each
 * resource shows amount, net per-second rate and how close it is to the storage
 * cap — because a resource sitting at its cap is quietly throwing away
 * production, and nothing else on screen would tell you. Population, workers,
 * troops and worker coverage are here for the same reason: coverage is why your
 * yield is lower than your land suggests, and without it the worker/troop
 * slider is a dial with no feedback.
 *
 * `update` is called at the simulation's 10Hz. It creates no nodes: the DOM is
 * built once in the constructor and every call writes `textContent` and a style
 * width on the handful of things that changed. Rebuilding this panel ten times
 * a second would jank the render loop it sits on top of.
 */

import type { ResourceType } from "kngdm-shared";
import { RESOURCE_TYPES, clamp01 } from "kngdm-shared";

import type { KingdomSummary } from "./derive";
import { formatAmount, formatPercent, formatRate } from "./format";

export interface ResourceBarCallbacks {
  /** Fires with a 0..1 worker share; the HUD turns it into an intent. */
  onWorkerRatioChange(ratio: number): void;
}

/**
 * Slider drags are throttled to roughly one intent per tick. Emitting on every
 * `input` event would push dozens of intents per second at the server's
 * per-kingdom queue cap for no gain — the simulation cannot act on them faster
 * than it ticks.
 */
const EMIT_INTERVAL_MS = 100;

/**
 * How long after the player last moved the slider we keep ignoring the server's
 * value. Without it, the ratio in flight arrives back one tick later and yanks
 * the thumb out from under the cursor.
 */
const SYNC_GRACE_MS = 400;

/** Cap proximity at which the bar starts warning that yield is being wasted. */
const NEAR_CAP_FRACTION = 0.9;

const RESOURCE_LABELS: Record<ResourceType, string> = {
  food: "Food",
  timber: "Timber",
  ore: "Ore",
  gold: "Gold",
  aether: "Aether",
};

interface ResourceCell {
  readonly root: HTMLElement;
  readonly amount: HTMLElement;
  readonly rate: HTMLElement;
  readonly capFill: HTMLElement;
  lastCapPercent: number;
}

export class ResourceBar {
  readonly element: HTMLElement;

  private readonly callbacks: ResourceBarCallbacks;
  private readonly cells = new Map<ResourceType, ResourceCell>();

  private readonly clockValue: HTMLElement;
  private readonly gloryValue: HTMLElement;
  private readonly landValue: HTMLElement;
  private readonly buildingsValue: HTMLElement;
  private readonly popValue: HTMLElement;
  private readonly workersValue: HTMLElement;
  private readonly troopsValue: HTMLElement;
  private readonly coverageValue: HTMLElement;

  private readonly slider: HTMLInputElement;
  private readonly splitReadout: HTMLElement;

  private interacting = false;
  private pendingRatio: number | null = null;
  private emitTimer: number | null = null;
  private lastEmitAt = Number.NEGATIVE_INFINITY;

  constructor(callbacks: ResourceBarCallbacks) {
    this.callbacks = callbacks;

    this.element = el("section", "panel panel--resources");

    const resourceRow = el("div", "rb-row rb-row--resources");
    for (const type of RESOURCE_TYPES) {
      const cell = this.buildResourceCell(type);
      this.cells.set(type, cell);
      resourceRow.append(cell.root);
    }

    const statRow = el("div", "rb-row rb-row--stats");
    this.clockValue = appendStat(statRow, "Time", "--:--");
    this.gloryValue = appendStat(statRow, "Glory", "0");
    this.landValue = appendStat(statRow, "Land", "0");
    this.buildingsValue = appendStat(statRow, "Built", "0");
    this.popValue = appendStat(statRow, "Pop", "0");
    this.workersValue = appendStat(statRow, "Workers", "0");
    this.troopsValue = appendStat(statRow, "Troops", "0");
    this.coverageValue = appendStat(statRow, "Coverage", "0%");

    const splitRow = el("div", "rb-row rb-row--split");
    const splitLabel = el("label", "rb-split__label", "Workers / Troops");
    splitLabel.htmlFor = "rb-split";

    this.slider = el("input", "rb-split__slider");
    this.slider.type = "range";
    this.slider.id = "rb-split";
    this.slider.min = "0";
    this.slider.max = "100";
    this.slider.step = "1";
    this.slider.value = "65";

    this.splitReadout = el("span", "rb-split__readout", "65% / 35%");
    splitRow.append(splitLabel, this.slider, this.splitReadout);

    this.element.append(resourceRow, statRow, splitRow);

    this.slider.addEventListener("input", this.handleSliderInput);
    this.slider.addEventListener("pointerdown", this.beginInteraction);
    this.slider.addEventListener("pointerup", this.endInteraction);
    this.slider.addEventListener("pointercancel", this.endInteraction);
    this.slider.addEventListener("focus", this.beginInteraction);
    this.slider.addEventListener("blur", this.endInteraction);
  }

  update(summary: KingdomSummary, clockText: string): void {
    for (const line of summary.resources) {
      const cell = this.cells.get(line.type);
      if (cell === undefined) continue;

      setText(cell.amount, formatAmount(line.amount));
      setText(cell.rate, formatRate(line.perSecond));
      cell.rate.classList.toggle("is-negative", line.perSecond < -0.05);

      // Rounded to whole percent before comparing: writing an identical style
      // string still dirties layout, and the bar is 60px wide anyway.
      const percent = Math.round(clamp01(line.capFraction) * 100);
      if (percent !== cell.lastCapPercent) {
        cell.lastCapPercent = percent;
        cell.capFill.style.width = `${percent}%`;
      }
      cell.root.classList.toggle("is-near-cap", line.capFraction >= NEAR_CAP_FRACTION);
    }

    setText(this.clockValue, clockText);
    setText(this.gloryValue, formatAmount(summary.glory.total));
    setText(this.landValue, formatAmount(summary.territory));
    setText(this.buildingsValue, formatAmount(summary.buildingCount));
    setText(this.popValue, formatAmount(summary.population));
    setText(this.workersValue, formatAmount(summary.workers));
    setText(this.troopsValue, formatAmount(summary.troops));

    setText(this.coverageValue, formatPercent(summary.efficiency));
    // Under-worked land is the single most common reason a kingdom's income
    // looks wrong, so it gets the same warning treatment as a full store.
    this.coverageValue.classList.toggle("is-warning", summary.efficiency < 0.999);

    this.syncSlider(summary.workerRatio);
  }

  destroy(): void {
    if (this.emitTimer !== null) {
      window.clearTimeout(this.emitTimer);
      this.emitTimer = null;
    }
    this.slider.removeEventListener("input", this.handleSliderInput);
    this.slider.removeEventListener("pointerdown", this.beginInteraction);
    this.slider.removeEventListener("pointerup", this.endInteraction);
    this.slider.removeEventListener("pointercancel", this.endInteraction);
    this.slider.removeEventListener("focus", this.beginInteraction);
    this.slider.removeEventListener("blur", this.endInteraction);
    this.element.remove();
  }

  // -------------------------------------------------------------------------
  // Slider
  // -------------------------------------------------------------------------

  private readonly beginInteraction = (): void => {
    this.interacting = true;
  };

  private readonly endInteraction = (): void => {
    this.interacting = false;
  };

  private readonly handleSliderInput = (): void => {
    const ratio = clamp01(Number(this.slider.value) / 100);
    this.writeSplitReadout(ratio);
    this.queueEmit(ratio);
  };

  /**
   * Rate-limit intents without dropping the last one: a drag emits at most one
   * per tick, and whatever the player settled on always gets sent.
   */
  private queueEmit(ratio: number): void {
    this.pendingRatio = ratio;
    if (this.emitTimer !== null) return;

    const wait = Math.max(0, EMIT_INTERVAL_MS - (performance.now() - this.lastEmitAt));
    this.emitTimer = window.setTimeout(() => {
      this.emitTimer = null;
      const pending = this.pendingRatio;
      this.pendingRatio = null;
      if (pending === null) return;
      this.lastEmitAt = performance.now();
      this.callbacks.onWorkerRatioChange(pending);
    }, wait);
  }

  private syncSlider(ratio: number): void {
    if (this.interacting || this.pendingRatio !== null) return;
    if (performance.now() - this.lastEmitAt < SYNC_GRACE_MS) return;

    const percent = Math.round(clamp01(ratio) * 100);
    if (Number(this.slider.value) !== percent) this.slider.value = `${percent}`;
    this.writeSplitReadout(percent / 100);
  }

  private writeSplitReadout(ratio: number): void {
    setText(this.splitReadout, `${formatPercent(ratio)} / ${formatPercent(1 - ratio)}`);
  }

  private buildResourceCell(type: ResourceType): ResourceCell {
    const root = el("div", `rb-res rb-res--${type}`);
    root.style.setProperty("--hue", RESOURCE_HUES[type]);

    // Icon and label share a row, so the eye lands on the glyph and the number
    // sits directly beneath it — the shape identifies the resource faster than
    // the word does once you have played twice.
    const head = el("div", "rb-res__head");
    head.append(iconFor(type), el("span", "rb-res__label", RESOURCE_LABELS[type]));

    const amount = el("span", "rb-res__amount", "0");
    const rate = el("span", "rb-res__rate", "0.0/s");

    const capTrack = el("div", "rb-cap");
    const capFill = el("div", "rb-cap__fill");
    capTrack.append(capFill);
    // The cap bar has no text; without this a screen reader hears nothing about
    // the thing that is silently eating the player's production.
    capTrack.title = `${RESOURCE_LABELS[type]} storage`;

    root.append(head, amount, rate, capTrack);
    return { root, amount, rate, capFill, lastCapPercent: -1 };
  }
}

// ---------------------------------------------------------------------------
// Resource icons
//
// Inline SVG keyed by `ResourceType`, so the compiler demands one for every
// resource and a sixth cannot be added without drawing it. They inherit
// `currentColor`, which is what lets each resource carry its own hue in the bar
// and still grey out consistently when a store runs dry.
// ---------------------------------------------------------------------------

const SVG_NS = "http://www.w3.org/2000/svg";

const ICON_PATHS: Record<ResourceType, string> = {
  // Wheat sheaf.
  food: "M12 2c1.6 2 1.6 4 0 6-1.6-2-1.6-4 0-6zM12 22V9M12 12c-2.6-.6-4-2.2-4.4-4.8 2.6.4 4 2 4.4 4.8zM12 12c2.6-.6 4-2.2 4.4-4.8-2.6.4-4 2-4.4 4.8zM12 17c-2.6-.6-4-2.2-4.4-4.8 2.6.4 4 2 4.4 4.8zM12 17c2.6-.6 4-2.2 4.4-4.8-2.6.4-4 2-4.4 4.8z",
  // Stacked logs, seen end-on.
  timber: "M4 7.5a3 1.8 0 106 0 3 1.8 0 10-6 0zM4 7.5v6a3 1.8 0 006 0v-6M14 7.5a3 1.8 0 106 0 3 1.8 0 10-6 0zM14 7.5v6a3 1.8 0 006 0v-6M9 17.5a3 1.8 0 106 0 3 1.8 0 10-6 0zM9 17.5v3",
  // Ore chunks.
  ore: "M7 4l5-2 5 2 2 6-7 12-7-12zM12 2v20M5 10h14",
  // Coin.
  gold: "M12 3a9 6 0 100 12 9 6 0 100-12zM3 9v6a9 6 0 0018 0V9M12 6.5v5",
  // Faceted crystal.
  aether: "M12 1.5l6 6.5-6 14.5-6-14.5zM6 8h12M12 1.5v20.5",
};

/**
 * Per-resource hue.
 *
 * A resource bar where five numbers share one colour is a table; giving each its
 * own hue means a glance at the bar tells you *which* store is low without
 * reading a single label. Kept desaturated so the row still reads as one panel
 * rather than five competing swatches.
 */
const RESOURCE_HUES: Record<ResourceType, string> = {
  food: "#8fbf6a",
  timber: "#c08d5a",
  ore: "#9aa4b0",
  gold: "#e0aa4a",
  aether: "#8fd8e8",
};

function iconFor(type: ResourceType): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("rb-res__icon");

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", ICON_PATHS[type]);
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "1.6");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.append(path);
  return svg;
}


// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Writing an unchanged string still invalidates layout in some engines. */
function setText(node: HTMLElement, value: string): void {
  if (node.textContent !== value) node.textContent = value;
}

function appendStat(row: HTMLElement, label: string, initial: string): HTMLElement {
  const stat = el("div", "rb-stat");
  const value = el("span", "rb-stat__value", initial);
  stat.append(el("span", "rb-stat__label", label), value);
  row.append(stat);
  return value;
}
